import { WithLineLimit } from '@cdab/headless-components'
import styled, { css } from 'styled-components'

import { getIconSize } from '@cdab/utils'
import { TdsIcon } from '@scania/tegel-react'
import { ArrowType, AuditItemState, AuditItemType } from './audit-item.types'

export function getAuditItemStateColor(state: AuditItemState): string {
  switch (state) {
    case AuditItemState.No:
      return 'var(--tds-negative)'

    case AuditItemState.Yes:
      return 'var(--tds-positive)'

    case AuditItemState.NotComplete:
      return 'var(--tds-warning)'

    default:
      /* Always show a border-left, but set alpha to 0 if it shouldn't be visible.
       * This way, the content will not jump around when showing or hiding the border
       */
      return 'rgba(0,0,0,0)' // Alpha 0
  }
}

interface WithAuditItemType {
  type: AuditItemType
}

function getClassNamesFor(type: AuditItemType): string {
  switch (type) {
    case AuditItemType.Header:
      return 'tds-headline-05'

    case AuditItemType.MajorSubheader:
      return 'tds-headline-06'

    case AuditItemType.MinorSubheader:
      return 'tds-headline-07'

    // eslint-disable-next-line no-fallthrough
    case AuditItemType.Normal:
    default:
      return 'tds-detail-02'
  }
}

export interface StyledAuditItemProps extends WithAuditItemType {
  state: AuditItemState
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  fullWidth: boolean
}

export const StyledAuditItem = styled.div<StyledAuditItemProps>`
  display: flex;
  flex-direction: column;
  transition: background-color 0.15s ease-in-out;

  ${({ fullWidth }) => css`
    width: ${fullWidth && '100%'};
  `}

  padding: 0 var(--tds-spacing-element-16);

  border-left: 8px solid ${({ state }) => getAuditItemStateColor(state)};
  border-bottom: 1px solid var(--tds-grey-400);

  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'inherit')};
`

const iconSize = 'var(--tds-spacing-element-12)'
const iconPadding = 'var(--tds-spacing-element-4)'

/**
 * iconSize + iconPadding should be 16px, but in the DOM it isn't. It's probably something at
 * play here I don't understand. But when adding 6px it gets the correct size 👍
 * Feel free to change this if you understand this better 😊
 */
const iconMagicNumber = '6px'

const iconButtonSize = `calc(${iconSize} + ${iconPadding} + ${iconMagicNumber} )`

function getIconButtonBackgroundColor(state: AuditItemState) {
  if (state === AuditItemState.NotSet) return 'white'
  return 'var(--tds-blue-800)'
}

function getIconButtonHoverBackgroundColor(state: AuditItemState) {
  if (state === AuditItemState.NotSet) return 'white'
  return 'var(--background-clickable-primary-primary-hover)'
}

function getIconButtonFocusBackgroundColor(state: AuditItemState) {
  if (state === AuditItemState.NotSet) return 'white'
  return 'var(--background-clickable-primary-focus)'
}

interface IconButtonProps {
  state: AuditItemState
  disabled?: boolean
}

export const IconButton = styled.button.attrs({
  className: 'primary sc-tds-button sc-tds-button-h sc-tds-button-s'
})<IconButtonProps>`
  &&& {
    appearance: none !important;
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    display: flex;
    align-items: center;
    justify-content: center;

    height: ${iconButtonSize} !important;
    width: ${iconButtonSize} !important;

    padding: ${iconPadding};
    border-color: var(--tds-grey-500) !important;
    margin-right: var(--tds-spacing-element-16);
    margin-top: var(--tds-spacing-element-12);
    border-radius: 4px !important;
    border-width: 1px !important;

    box-shadow: 'none' !important;
    align-self: flex-start;

    color: var(--tds-btn-icon-primary-color);
    background-color: ${({ state }) => getIconButtonBackgroundColor(state)};
    font-size: ${iconSize};
    border-color: var(--tds-btn-primary-border-color);
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

    :hover {
      background-color: ${({ state }) =>
        getIconButtonHoverBackgroundColor(state)};
    }

    :focus {
      background-color: ${({ state }) =>
        getIconButtonFocusBackgroundColor(state)};
    }
  }
`

interface RightSideArrowProps {
  direction: ArrowType
}

export const RightSideArrow = styled(TdsIcon)<RightSideArrowProps>`
  margin-top: var(--tds-spacing-element-12);
  min-width: ${getIconSize('medium')};
  min-width: ${getIconSize('medium')};
  height: ${getIconSize('medium')};
  height: ${getIconSize('medium')};

  transition: transform 0.15s ease-in-out;
  transform: ${({ direction }) => {
    switch (direction) {
      case ArrowType.Down:
        return 'rotate(0deg)'

      case ArrowType.Up:
        return 'rotate(180deg)'

      case ArrowType.Right: // fallthrough
      default:
        return 'rotate(-90deg)'
    }
  }};
`

const NoIcon = styled.div`
  width: ${iconSize};
  height: ${iconSize};
`

export function getAuditItemStateIcon(state: AuditItemState): React.ReactNode {
  switch (state) {
    case AuditItemState.No:
      return <TdsIcon name='minus' slot='icon' size={'16'} />

    case AuditItemState.NotSet:
      // If no state, render
      return <NoIcon />

    case AuditItemState.Yes:
      return <TdsIcon name='tick' slot='icon' size={'16'} />

    default:
      // eslint-disable-next-line react/jsx-no-useless-fragment
      return <></>
  }
}

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  align-items: start;
  margin-top: var(--tds-spacing-element-16);
  margin-bottom: var(--tds-spacing-element-16);

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const PrefixWrapper = styled.span.attrs<WithAuditItemType>(
  ({ type }) => ({
    className: getClassNamesFor(type)
  })
)<WithAuditItemType>`
  min-width: var(--tds-spacing-element-32);
  margin-right: var(--tds-spacing-element-8);
  text-align: left;
  align-self: flex-start;
`

export const Content = styled(WithLineLimit).attrs<WithAuditItemType>(
  ({ type }) => ({
    className: getClassNamesFor(type)
  })
)<WithAuditItemType>`
  width: 100%;
`

export const StyledIconInfo = styled(TdsIcon)`
  width: ${getIconSize('medium')};
  height: ${getIconSize('medium')};
  margin-right: var(--tds-spacing-element-8);
`

export const ChildrenWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`
export const RightItem = styled.div`
  svg {
    margin-top: var(--tds-spacing-element-12);
    min-width: ${getIconSize('medium')};
    height: ${getIconSize('medium')};
  }
`
